/**
 * ══════════════════════════════════════════════════════════════
 * NETUNO — Tipos para Recomendações Operacionais
 * ══════════════════════════════════════════════════════════════
 *
 * Modelo padrão para ações de mitigação geradas automaticamente
 * a partir de insights e diagnósticos analíticos.
 *
 * Uso: import { OperationalRecommendation } from '@/types/recommendations';
 * ══════════════════════════════════════════════════════════════
 */

// ─── Priority ───────────────────────────────────────────────

export type RecommendationPriority = 'critical' | 'high' | 'medium' | 'low';

export const PRIORITY_LABELS: Record<RecommendationPriority, string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

export const PRIORITY_ORDER: Record<RecommendationPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

// ─── Effort Level ───────────────────────────────────────────

export type EffortLevel = 'low' | 'medium' | 'high';

export const EFFORT_LABELS: Record<EffortLevel, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

// ─── Impact Estimate ────────────────────────────────────────

export interface ImpactEstimate {
  /** Short label describing the expected outcome */
  label: string;
  /** Estimated score reduction (0-100) */
  scoreReduction?: number;
  /** Estimated risk mitigation percentage */
  mitigationPct?: number;
  /** Qualitative description */
  description: string;
}

// ─── Target Entity ──────────────────────────────────────────

export interface RecommendationTarget {
  type: 'contract' | 'uo' | 'hub' | 'cluster' | 'portfolio';
  id: string;
  name: string;
  drillDownRoute?: string;
}

// ─── Operational Recommendation ─────────────────────────────

export interface OperationalRecommendation {
  /** Unique ID */
  id: string;
  /** Related insight that originated the recommendation chain */
  relatedInsightId: string;
  /** Related diagnostic that informed this recommendation */
  relatedDiagnosticId: string;
  /** Short action-oriented title */
  title: string;
  /** Detailed description of the recommended action */
  description: string;
  /** Priority level */
  priority: RecommendationPriority;
  /** Estimated impact if implemented */
  estimatedImpact: ImpactEstimate;
  /** Effort required to implement */
  effortLevel: EffortLevel;
  /** Entities targeted by this recommendation */
  targetEntities: RecommendationTarget[];
  /** Suggested owner role (e.g., "Gerente de Contrato") */
  suggestedOwner: string;
  /** Category tag */
  category: RecommendationCategory;
  /** Route for drill-down navigation */
  drillDownRoute?: string;
  /** When the recommendation was generated */
  generatedAt: string;
  /** Whether this action is mandatory (vs recommended) */
  mandatory?: boolean;
  /** Origin system that generated this recommendation */
  source?: 'mre' | 'governance' | 'financial_cycle';
  /** Execution status — computed at runtime, never persisted */
  statusExecucao?: 'pendente' | 'resolvida';
  /** Effectiveness classification — computed at runtime */
  efetividade?: 'effective' | 'neutral' | 'ineffective';
  /** Estimated score variation (negative = improvement) */
  variacaoScore?: number;
}

// ─── Category ───────────────────────────────────────────────

export type RecommendationCategory =
  | 'schedule_intervention'
  | 'cost_control'
  | 'quality_review'
  | 'resource_reallocation'
  | 'contract_renegotiation'
  | 'executive_escalation'
  | 'monitoring_increase'
  | 'preventive_action';

export const CATEGORY_LABELS: Record<RecommendationCategory, string> = {
  schedule_intervention: 'Intervenção de Cronograma',
  cost_control: 'Controle de Custos',
  quality_review: 'Revisão de Qualidade',
  resource_reallocation: 'Realocação de Recursos',
  contract_renegotiation: 'Renegociação Contratual',
  executive_escalation: 'Escalação Executiva',
  monitoring_increase: 'Intensificação de Monitoramento',
  preventive_action: 'Ação Preventiva',
};
